/**
 * Lightweight, device-local "have I seen the latest message in this chat"
 * tracker for the chat inbox's unread dot. Deliberately local-only (not
 * synced to Firestore) — this only needs to answer "should this device show
 * a dot," not maintain cross-device read receipts, so it avoids adding any
 * new Firestore writes/schema for what is otherwise a simple indicator.
 *
 * Keys are chat ids; the E-Board room uses `<chatId>#eboard` (utils/chatUnread).
 * markChatRead notifies subscribers AFTER the write resolves so the badge
 * context can recompute without racing the AsyncStorage write.
 */
import AsyncStorage from "@react-native-async-storage/async-storage";
import { eboardBaseId, eboardKey } from "./chatUnread";

const KEY_PREFIX = "chat_last_read_";
const listeners = new Set();
const eboardMigrations = new Map(); // E-Board chatId -> in-flight first-use copy

export function subscribeChatRead(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// `meta` (optional) rides along to subscribers, e.g. { messageIds } from the
// E-Board room so the badge can clear notices that pre-date its own key.
export async function markChatRead(chatId, meta) {
  if (!chatId) return;
  // A first-use E-Board copy in flight must not land after (and over) this read.
  const pending = eboardMigrations.get(eboardBaseId(chatId));
  if (pending) await pending.catch(() => {});
  try {
    await AsyncStorage.setItem(KEY_PREFIX + chatId, Date.now().toString());
  } catch {
    // Best-effort — an unread dot lingering one extra visit is harmless.
  }
  listeners.forEach((fn) => {
    try {
      fn(chatId, meta);
    } catch {
      // A subscriber must never break marking read.
    }
  });
}

export async function getChatLastReadAt(chatId) {
  if (!chatId) return 0;
  try {
    const value = await AsyncStorage.getItem(KEY_PREFIX + chatId);
    return value ? parseInt(value, 10) : 0;
  } catch {
    return 0;
  }
}

// The E-Board room's marker. Before `<chatId>#eboard` existed the room stamped
// the general marker, so a missing E-Board marker falls back to it. The badge
// (UnreadChatsContext) and the row (useEboardUnread) both read this.
// The fallback is copied into the E-Board key the first time it is used, so a
// later read of the general room never counts as reading the E-Board.
export function getEboardLastReadAt(chatId) {
  if (!chatId) return Promise.resolve(0);
  // Concurrent first uses share one read + copy, so the copied value can't race.
  if (!eboardMigrations.has(chatId)) {
    const p = readEboardMarker(chatId).finally(() => eboardMigrations.delete(chatId));
    eboardMigrations.set(chatId, p);
  }
  return eboardMigrations.get(chatId);
}

async function readEboardMarker(chatId) {
  const own = await getChatLastReadAt(eboardKey(chatId));
  if (own) return own;
  const general = await getChatLastReadAt(chatId);
  if (general) {
    try {
      await AsyncStorage.setItem(KEY_PREFIX + eboardKey(chatId), general.toString());
    } catch {
      // Best-effort: the next read falls back again.
    }
  }
  return general;
}
